import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, XCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RecurringPage() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { t } = useLanguage();
  const { isHR, isAdmin } = useAuth();
  
  const [form, setForm] = useState({
    employee_id: '',
    item_type: 'deduction',
    name: '',
    amount: '',
    percentage: '',
    start_month: new Date().toISOString().slice(0, 7),
    end_month: '',
    notes: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [itemsRes, employeesRes] = await Promise.all([
          axios.get(`${API}/recurring-items`),
          axios.get(`${API}/employees`, { params: { is_active: true } })
        ]);
        setItems(itemsRes.data);
        setEmployees(employeesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error(t('errorOccurred'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [t]);

  const refetchData = async () => {
    try {
      const [itemsRes, employeesRes] = await Promise.all([
        axios.get(`${API}/recurring-items`),
        axios.get(`${API}/employees`, { params: { is_active: true } })
      ]);
      setItems(itemsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('errorOccurred'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/recurring-items`, {
        ...form,
        amount: form.amount ? parseFloat(form.amount) : null,
        percentage: form.percentage ? parseFloat(form.percentage) : null,
        end_month: form.end_month || null
      });
      toast.success(t('savedSuccessfully'));
      setDialogOpen(false);
      setForm({
        employee_id: '',
        item_type: 'deduction',
        name: '',
        amount: '',
        percentage: '',
        start_month: new Date().toISOString().slice(0, 7),
        end_month: '',
        notes: ''
      });
      refetchData();
    } catch (error) {
      console.error('Error creating item:', error);
      toast.error(t('errorOccurred'));
    }
  };

  const handleDeactivate = async (id) => {
    try {
      await axios.put(`${API}/recurring-items/${id}/deactivate`);
      toast.success(t('savedSuccessfully'));
      refetchData();
    } catch (error) {
      console.error('Error deactivating item:', error);
      toast.error(t('errorOccurred'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await axios.delete(`${API}/recurring-items/${id}`);
      toast.success(t('deletedSuccessfully'));
      refetchData();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp ? `${emp.first_name} ${emp.last_name}` : empId;
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(value) + ' TND';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#E31E24] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="recurring-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-[#1A1A1A]">{t('recurring')}</h1>
        {isHR() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#E31E24] hover:bg-[#C41218] text-[#1A1A1A] btn-primary-glow" data-testid="add-recurring-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('newRecurringItem')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200">
              <DialogHeader>
                <DialogTitle className="text-[#1A1A1A]">{t('newRecurringItem')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('employees')}</Label>
                  <Select value={form.employee_id} onValueChange={(v) => setForm(prev => ({ ...prev, employee_id: v }))}>
                    <SelectTrigger className="bg-white border-gray-200 text-[#1A1A1A]">
                      <SelectValue placeholder={t('employees')} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('itemType')}</Label>
                  <Select value={form.item_type} onValueChange={(v) => setForm(prev => ({ ...prev, item_type: v }))}>
                    <SelectTrigger className="bg-white border-gray-200 text-[#1A1A1A]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="deduction">{t('deduction')}</SelectItem>
                      <SelectItem value="addition">{t('addition')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('name')}</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                    className="bg-white border-gray-200 text-[#1A1A1A]"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('amount')}</Label>
                    <Input
                      type="number"
                      step="0.001"
                      value={form.amount}
                      onChange={(e) => setForm(prev => ({ ...prev, amount: e.target.value }))}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      placeholder="TND"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('percentage')}</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={form.percentage}
                      onChange={(e) => setForm(prev => ({ ...prev, percentage: e.target.value }))}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      placeholder="%"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('startMonth')}</Label>
                    <Input
                      type="month"
                      value={form.start_month}
                      onChange={(e) => setForm(prev => ({ ...prev, start_month: e.target.value }))}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500">{t('endMonth')}</Label>
                    <Input
                      type="month"
                      value={form.end_month}
                      onChange={(e) => setForm(prev => ({ ...prev, end_month: e.target.value }))}
                      className="bg-white border-gray-200 text-[#1A1A1A]"
                      placeholder="Optional"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-[#E31E24] hover:bg-[#C41218] text-[#1A1A1A]">
                  {t('save')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Table */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-transparent">
                  <TableHead className="table-header">{t('employees')}</TableHead>
                  <TableHead className="table-header">{t('name')}</TableHead>
                  <TableHead className="table-header">{t('itemType')}</TableHead>
                  <TableHead className="table-header">{t('amount')}</TableHead>
                  <TableHead className="table-header">{t('percentage')}</TableHead>
                  <TableHead className="table-header">{t('startMonth')}</TableHead>
                  <TableHead className="table-header">{t('endMonth')}</TableHead>
                  <TableHead className="table-header text-right">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-400">
                      {t('noData')}
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item.id} className="table-row" data-testid={`recurring-row-${item.id}`}>
                      <TableCell className="text-[#1A1A1A]">{getEmployeeName(item.employee_id)}</TableCell>
                      <TableCell className="text-[#1A1A1A]">{item.name}</TableCell>
                      <TableCell>
                        {item.item_type === 'deduction' ? (
                          <Badge className="badge-warning">{t('deduction')}</Badge>
                        ) : (
                          <Badge className="badge-success">{t('addition')}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-[#1A1A1A]">{formatCurrency(item.amount)}</TableCell>
                      <TableCell className="font-mono text-[#1A1A1A]">{item.percentage ? `${item.percentage}%` : '-'}</TableCell>
                      <TableCell className="text-gray-500 font-mono">{item.start_month}</TableCell>
                      <TableCell className="text-gray-500 font-mono">{item.end_month || '-'}</TableCell>
                      <TableCell className="text-right space-x-2">
                        {isHR() && item.is_active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeactivate(item.id)}
                            className="text-orange-500 hover:text-orange-400"
                            data-testid={`deactivate-${item.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            {t('deactivate')}
                          </Button>
                        )}
                        {isAdmin() && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(item.id)}
                            className="text-red-500 hover:text-red-400"
                            data-testid={`delete-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            {t('delete')}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
